import { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { RefreshCw, CheckCircle2, AlertTriangle, AlertCircle, Clock, PauseCircle } from 'lucide-react'
import { cn, timeAgo } from '../lib/utils'

export type ConnectionStatus =
  | 'pending_consent'
  | 'consent_sent'
  | 'consent_error'
  | 'validating'
  | 'active'
  | 'degraded'
  | 'error'
  | 'paused'
  | 'verification_failed'
  | 'domain_mismatch'

interface ConnectionStatusConfig {
  dotColor: string
  pulseColor?: string
  label: string
  textColor: string
  bgColor: string
  icon: React.ReactNode
}

const statusConfig: Record<ConnectionStatus, ConnectionStatusConfig> = {
  active: {
    dotColor: 'bg-green-500',
    label: 'Connected',
    textColor: 'text-green-700',
    bgColor: 'bg-green-50',
    icon: <CheckCircle2 className="w-3.5 h-3.5 text-green-600" aria-hidden="true" />,
  },
  paused: {
    dotColor: 'bg-gray-400',
    label: 'Paused',
    textColor: 'text-gray-600',
    bgColor: 'bg-gray-50',
    icon: <PauseCircle className="w-3.5 h-3.5 text-gray-500" aria-hidden="true" />,
  },
  validating: {
    dotColor: 'bg-yellow-400',
    pulseColor: 'bg-yellow-400',
    label: 'Validating...',
    textColor: 'text-yellow-700',
    bgColor: 'bg-yellow-50',
    icon: <Clock className="w-3.5 h-3.5 text-yellow-600" aria-hidden="true" />,
  },
  consent_sent: {
    dotColor: 'bg-yellow-400',
    pulseColor: 'bg-yellow-400',
    label: 'Consent sent',
    textColor: 'text-yellow-700',
    bgColor: 'bg-yellow-50',
    icon: <Clock className="w-3.5 h-3.5 text-yellow-600" aria-hidden="true" />,
  },
  consent_error: {
    dotColor: 'bg-red-500',
    label: 'Consent error',
    textColor: 'text-red-700',
    bgColor: 'bg-red-50',
    icon: <AlertTriangle className="w-3.5 h-3.5 text-red-600" aria-hidden="true" />,
  },
  pending_consent: {
    dotColor: 'bg-gray-300',
    label: 'Awaiting consent',
    textColor: 'text-gray-500',
    bgColor: 'bg-gray-50',
    icon: <AlertCircle className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />,
  },
  error: {
    dotColor: 'bg-red-500',
    label: 'Error',
    textColor: 'text-red-700',
    bgColor: 'bg-red-50',
    icon: <AlertTriangle className="w-3.5 h-3.5 text-red-600" aria-hidden="true" />,
  },
  // Watchdog verdict (DW-OBS-20260808-A): events stopped arriving at this
  // account's own normal cadence. Honest amber, not green: coverage is not
  // currently being observed.
  degraded: {
    dotColor: 'bg-amber-500',
    label: 'Degraded',
    textColor: 'text-amber-700',
    bgColor: 'bg-amber-50',
    icon: <AlertTriangle className="w-3.5 h-3.5 text-amber-600" aria-hidden="true" />,
  },
  // Graph miss after admin consent (v51+): retryable verify failure — not
  // "still waiting for consent". Must not fall through to pending_consent.
  verification_failed: {
    dotColor: 'bg-amber-500',
    label: "Couldn't verify",
    textColor: 'text-amber-700',
    bgColor: 'bg-amber-50',
    icon: <AlertTriangle className="w-3.5 h-3.5 text-amber-600" aria-hidden="true" />,
  },
  domain_mismatch: {
    dotColor: 'bg-red-500',
    label: 'Domain mismatch',
    textColor: 'text-red-700',
    bgColor: 'bg-red-50',
    icon: <AlertTriangle className="w-3.5 h-3.5 text-red-600" aria-hidden="true" />,
  },
}

interface ConnectionStatusBadgeProps {
  status: ConnectionStatus | string
  error?: string | null
  lastSyncAt?: string | null
  onRefresh?: () => void
  showRefresh?: boolean
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export function ConnectionStatusBadge({
  status,
  error,
  lastSyncAt,
  onRefresh,
  showRefresh = true,
  size = 'md',
  className,
}: ConnectionStatusBadgeProps) {
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [showError, setShowError] = useState(false)

  const config = statusConfig[status as ConnectionStatus] ?? statusConfig.pending_consent

  const handleRefresh = useCallback(async () => {
    if (!onRefresh || isRefreshing) return
    setIsRefreshing(true)
    try {
      await onRefresh()
    } finally {
      // Small delay so the user sees the action happened
      setTimeout(() => setIsRefreshing(false), 400)
    }
  }, [onRefresh, isRefreshing])

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs gap-1.5',
    md: 'px-2.5 py-1 text-sm gap-2',
    lg: 'px-3 py-1.5 text-base gap-2',
  }

  const dotSize = {
    sm: 'w-1.5 h-1.5',
    md: 'w-2 h-2',
    lg: 'w-2.5 h-2.5',
  }

  return (
    <div className={cn('relative inline-flex flex-col', className)}>
      <div
        className={cn(
          'inline-flex items-center rounded-full font-medium',
          config.bgColor,
          config.textColor,
          sizeClasses[size],
        )}
      >
        {/* Status dot with optional pulse */}
        <span className="relative flex items-center justify-center">
          <span
            className={cn(
              'rounded-full',
              config.dotColor,
              dotSize[size],
              (status === 'validating' || status === 'consent_sent') && 'animate-pulse',
            )}
          />
          {config.pulseColor && (
            <span
              className={cn(
                'absolute rounded-full opacity-40 animate-ping',
                config.pulseColor,
                dotSize[size],
              )}
              style={{ animationDuration: '2s' }}
            />
          )}
        </span>

        {/* Icon */}
        {config.icon}

        {/* Label */}
        <span>{config.label}</span>

        {/* Refresh button */}
        {showRefresh && onRefresh && (
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={cn(
              'ml-1 rounded-full p-0.5 transition-colors',
              'hover:bg-black/5 focus:outline-none focus:ring-1 focus:ring-brass',
              'disabled:opacity-50',
              config.textColor,
            )}
            aria-label="Refresh connection status"
            title="Refresh status"
          >
            <RefreshCw
              className={cn(
                'w-3 h-3',
                isRefreshing && 'animate-spin',
              )}
              aria-hidden="true"
            />
          </button>
        )}
      </div>

      {/* Error tooltip / expandable */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <button
              type="button"
              onClick={() => setShowError((v) => !v)}
              className="mt-1 text-xs text-red-600 hover:text-red-700 underline underline-offset-2 text-left"
            >
              {showError ? 'Hide error' : 'View error details'}
            </button>
            <AnimatePresence>
              {showError && (
                <motion.p
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="mt-1 text-xs text-red-600 bg-red-50 rounded-md px-2 py-1.5 border border-red-100 max-w-xs"
                >
                  {error}
                </motion.p>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Last sync timestamp */}
      <AnimatePresence>
        {status === 'active' && lastSyncAt && (
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mt-1 text-xs text-gray-400"
          >
            Last sync: {lastSyncAt ? timeAgo(lastSyncAt) : 'Never'}
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  )
}
